import React, { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Chip, MenuItem, TextField, Typography } from '@mui/material';
import styled from 'styled-components';
import EdButton from '@Components/UI/Buttons/EdButton/EdButton';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import {
	buildCredentialsPatch,
	CardNote,
	CardSectionTitle,
	emptySecretDraft,
	ProviderCard,
	SecretDraft,
	SecretField,
	storedSecret,
} from '../VideoProviders/Components/ProviderCardShared';
import TemplateMappingTable from './TemplateMappingTable';
import {
	WhatsappSettingsRequester,
	whatsappErrorMessage,
} from './whatsapp.requester';
import {
	TestResultVariable,
	WhatsappCatalog,
	WhatsappProviderSetting,
	WhatsappSettingsResponse,
	WhatsappTemplateSummary,
} from './whatsapp.types';

type Props = {
	setting: WhatsappProviderSetting;
	variables: TestResultVariable[];
	/** The center-attendance template's own catalog. */
	attendanceVariables: TestResultVariable[];
	/** The PUT/webhook responses carry the fresh masked row; the tab keeps it. */
	onSaved: (response: WhatsappSettingsResponse) => void;
};

const requester = WhatsappSettingsRequester.getInstance();

const templateKey = (name?: string, language?: string) =>
	name && language ? `${name}|${language}` : '';

const originOf = (url?: string) => {
	if (!url) return '';
	try {
		return new URL(url).origin;
	} catch {
		return '';
	}
};

/**
 * YCloud provider card. The API key unlocks the provider account: senders
 * and templates are picked from YCloud's own lists (Load from YCloud), each
 * template placeholder is assigned a variable, and the status webhook is
 * registered against the student API in one click. Enabling runs the
 * backend's readiness check (approved template, matching mapping, webhook).
 */
const YCloudCard: FC<Props> = ({ setting, variables, attendanceVariables, onSaved }) => {
	const { displaySnackbar } = useSnackbar();
	const config = setting.config;

	const [enabled, setEnabled] = useState(setting.enabled);
	const [apiKeyDraft, setApiKeyDraft] = useState<SecretDraft>(emptySecretDraft());
	const [catalog, setCatalog] = useState<WhatsappCatalog | null>(null);
	const [loadingCatalog, setLoadingCatalog] = useState(false);
	const [saving, setSaving] = useState(false);
	const [registering, setRegistering] = useState(false);

	const [senderNumber, setSenderNumber] = useState(config.sender_number ?? '');
	const [senderName, setSenderName] = useState(config.sender_name ?? '');
	const [wabaId, setWabaId] = useState(config.waba_id ?? '');
	const [templateName, setTemplateName] = useState(config.template_name ?? '');
	const [templateLanguage, setTemplateLanguage] = useState(
		config.template_language ?? ''
	);
	const [mapping, setMapping] = useState<string[]>(config.template_params ?? []);
	const [attendanceTemplateName, setAttendanceTemplateName] = useState(
		config.attendance_template_name ?? ''
	);
	const [attendanceTemplateLanguage, setAttendanceTemplateLanguage] = useState(
		config.attendance_template_language ?? ''
	);
	const [attendanceMapping, setAttendanceMapping] = useState<string[]>(
		config.attendance_template_params ?? []
	);
	const [baseUrl, setBaseUrl] = useState(originOf(config.webhook_url));
	const [attendanceBaseUrl, setAttendanceBaseUrl] = useState(
		originOf(config.attendance_webhook_url)
	);
	const [registeringAttendance, setRegisteringAttendance] = useState(false);
	const [lowBalanceThreshold, setLowBalanceThreshold] = useState(
		String(config.low_balance_threshold ?? 10)
	);

	useEffect(() => {
		setEnabled(setting.enabled);
		setApiKeyDraft(emptySecretDraft());
		setSenderNumber(setting.config.sender_number ?? '');
		setSenderName(setting.config.sender_name ?? '');
		setWabaId(setting.config.waba_id ?? '');
		setTemplateName(setting.config.template_name ?? '');
		setTemplateLanguage(setting.config.template_language ?? '');
		setMapping(setting.config.template_params ?? []);
		setAttendanceTemplateName(setting.config.attendance_template_name ?? '');
		setAttendanceTemplateLanguage(
			setting.config.attendance_template_language ?? ''
		);
		setAttendanceMapping(setting.config.attendance_template_params ?? []);
		setBaseUrl((current) => current || originOf(setting.config.webhook_url));
		setAttendanceBaseUrl(
			(current) => current || originOf(setting.config.attendance_webhook_url)
		);
		setLowBalanceThreshold(String(setting.config.low_balance_threshold ?? 10));
	}, [setting]);

	const hasStoredKey = !!storedSecret(setting.credentials, 'api_key')?.has_value;
	const typedKey = apiKeyDraft.value.trim();
	const keyAvailable = !!typedKey || (hasStoredKey && !apiKeyDraft.cleared);

	const senders = catalog?.senders ?? [];
	const templates = useMemo(
		() =>
			(catalog?.templates ?? []).filter(
				(template) => !wabaId || !template.waba_id || template.waba_id === wabaId
			),
		[catalog, wabaId]
	);
	const selectedTemplate: WhatsappTemplateSummary | undefined = useMemo(
		() =>
			templates.find(
				(template) =>
					template.name === templateName && template.language === templateLanguage
			),
		[templates, templateName, templateLanguage]
	);
	const placeholderCount = selectedTemplate?.body_param_count;
	const mappingComplete =
		placeholderCount === undefined
			? mapping.length > 0 && mapping.every(Boolean)
			: mapping.length === placeholderCount && mapping.every(Boolean);
	const selectedAttendanceTemplate: WhatsappTemplateSummary | undefined = useMemo(
		() =>
			templates.find(
				(template) =>
					template.name === attendanceTemplateName &&
					template.language === attendanceTemplateLanguage
			),
		[templates, attendanceTemplateName, attendanceTemplateLanguage]
	);
	const attendancePlaceholderCount = selectedAttendanceTemplate?.body_param_count;
	// The attendance template is OPTIONAL: no name chosen = nothing to complete.
	const attendanceMappingComplete =
		!attendanceTemplateName ||
		(attendancePlaceholderCount === undefined
			? attendanceMapping.every(Boolean)
			: attendanceMapping.length === attendancePlaceholderCount &&
			  attendanceMapping.every(Boolean));

	const loadCatalog = async () => {
		setLoadingCatalog(true);
		try {
			const {
				data: { data },
			} = await requester.catalog({
				provider: 'ycloud',
				api_key: typedKey || undefined,
			});
			setCatalog(data);
			const balance =
				data.balance.balance !== null
					? `${data.balance.balance} ${data.balance.currency ?? ''}`.trim()
					: 'unknown';
			displaySnackbar(
				'success',
				`Connected to YCloud — balance ${balance}, ${data.senders.length} sender(s), ${data.templates.length} template(s)`
			);
		} catch (error: any) {
			displaySnackbar('error', whatsappErrorMessage(error, 'Unable to reach YCloud'));
		} finally {
			setLoadingCatalog(false);
		}
	};

	const chooseSender = (phoneNumber: string) => {
		const sender = senders.find((candidate) => candidate.phone_number === phoneNumber);
		setSenderNumber(phoneNumber);
		setSenderName(sender?.display_name ?? '');
		setWabaId(sender?.waba_id ?? '');
	};

	const chooseTemplate = (key: string) => {
		const template = templates.find(
			(candidate) => templateKey(candidate.name, candidate.language) === key
		);
		setTemplateName(template?.name ?? '');
		setTemplateLanguage(template?.language ?? '');
		const count = template?.body_param_count ?? 0;
		// Keep a stored mapping that still fits; otherwise start blank.
		setMapping((current) =>
			current.length === count ? current : Array.from({ length: count }, () => '')
		);
	};

	const chooseAttendanceTemplate = (key: string) => {
		if (!key) {
			// "None" — the attendance channel is optional; clearing removes it.
			setAttendanceTemplateName('');
			setAttendanceTemplateLanguage('');
			setAttendanceMapping([]);
			return;
		}
		const template = templates.find(
			(candidate) => templateKey(candidate.name, candidate.language) === key
		);
		setAttendanceTemplateName(template?.name ?? '');
		setAttendanceTemplateLanguage(template?.language ?? '');
		const count = template?.body_param_count ?? 0;
		setAttendanceMapping((current) =>
			current.length === count ? current : Array.from({ length: count }, () => '')
		);
	};

	const registerWebhook = async () => {
		setRegistering(true);
		try {
			const {
				data: { data },
			} = await requester.registerWebhook({
				provider: 'ycloud',
				api_key: typedKey || undefined,
				base_url: baseUrl.trim(),
			});
			displaySnackbar(
				'success',
				`${data.reused ? 'Reusing' : 'Registered'} webhook ${data.url}`
			);
			// The secret (and a typed key) were stored server-side — refresh.
			const {
				data: { data: fresh },
			} = await requester.getAll();
			onSaved(fresh);
		} catch (error: any) {
			displaySnackbar('error', whatsappErrorMessage(error, 'Unable to register the webhook'));
		} finally {
			setRegistering(false);
		}
	};

	const registerAttendanceWebhook = async () => {
		setRegisteringAttendance(true);
		try {
			const {
				data: { data },
			} = await requester.registerWebhook({
				provider: 'ycloud',
				api_key: typedKey || undefined,
				base_url: attendanceBaseUrl.trim(),
				target: 'attendance',
			});
			displaySnackbar(
				'success',
				`${data.reused ? 'Reusing' : 'Registered'} attendance webhook ${data.url}`
			);
			const {
				data: { data: fresh },
			} = await requester.getAll();
			onSaved(fresh);
		} catch (error: any) {
			displaySnackbar(
				'error',
				whatsappErrorMessage(error, 'Unable to register the attendance webhook')
			);
		} finally {
			setRegisteringAttendance(false);
		}
	};

	const save = async () => {
		setSaving(true);
		try {
			const {
				data: { data },
			} = await requester.update({
				provider: 'ycloud',
				enabled,
				credentials: buildCredentialsPatch({ api_key: apiKeyDraft }),
				config: {
					sender_number: senderNumber,
					sender_name: senderName,
					waba_id: wabaId,
					template_name: templateName,
					template_language: templateLanguage,
					// Only a complete mapping is stored; a half-assigned one is left
					// out (axios drops undefined keys) so partial progress on the
					// other fields can still be saved. Enabling checks completeness.
					template_params: mapping.every(Boolean) ? mapping : undefined,
					// Optional center-attendance template: an empty name CLEARS the
					// three keys server-side (null = delete); a chosen one is stored
					// with its mapping once every placeholder is assigned.
					attendance_template_name: (attendanceTemplateName || null) as any,
					attendance_template_language: ((attendanceTemplateName &&
						attendanceTemplateLanguage) ||
						null) as any,
					attendance_template_params: (!attendanceTemplateName
						? null
						: attendanceMapping.every(Boolean)
						? attendanceMapping
						: undefined) as any,
					low_balance_threshold: Number(lowBalanceThreshold) >= 0 ? Number(lowBalanceThreshold) : undefined,
				},
			});
			onSaved(data);
			displaySnackbar(
				'success',
				enabled
					? 'WhatsApp notifications enabled'
					: 'WhatsApp settings saved (channel disabled)'
			);
		} catch (error: any) {
			displaySnackbar('error', whatsappErrorMessage(error, 'Unable to save WhatsApp settings'));
		} finally {
			setSaving(false);
		}
	};

	const webhookRegistered = !!storedSecret(setting.credentials, 'webhook_secret')?.has_value;
	const attendanceWebhookRegistered = !!storedSecret(
		setting.credentials,
		'attendance_webhook_secret'
	)?.has_value;

	return (
		<ProviderCard
			title="YCloud"
			subtitle="WhatsApp Business API — exam-result and center-attendance notifications"
			enabled={enabled}
			onEnabledChange={setEnabled}
			onSave={save}
			saving={saving}
			disabled={!setting.encryption_ready}
			disabledReason="VIDEO_PROVIDERS_ENCRYPTION_KEY is not set on this server — secrets cannot be stored."
		>
			<CardSectionTitle>1. Connection</CardSectionTitle>
			<SecretField
				label="API key"
				stored={storedSecret(setting.credentials, 'api_key')}
				draft={apiKeyDraft}
				onChange={setApiKeyDraft}
				helperText="YCloud → Developers → API Keys"
			/>
			<FlexLayout alignItems="center" gridGap="1rem">
				<EdButton
					variant="outlined"
					edcolor="purple"
					disabled={!keyAvailable || loadingCatalog}
					onClick={loadCatalog}
				>
					{loadingCatalog ? 'Loading…' : 'Load from YCloud'}
				</EdButton>
				{catalog && (
					<Typography variant="caption" color="textSecondary">
						Balance:{' '}
						{catalog.balance.balance !== null
							? `${catalog.balance.balance} ${catalog.balance.currency ?? ''}`
							: 'unknown'}
					</Typography>
				)}
			</FlexLayout>
			<CardNote>
				Loads the senders and templates on the account so they can be picked
				below. Uses the key typed above, or the stored one when the field is
				blank.
			</CardNote>
			<FlexLayout alignItems="flex-start" gridGap="1rem">
				<TextField
					size="small"
					type="number"
					label="Low-balance warning level"
					value={lowBalanceThreshold}
					inputProps={{ min: 0, step: 1 }}
					onChange={(event) => setLowBalanceThreshold(event.target.value)}
					helperText="A warning shows on this page when the prepaid balance drops below this. An empty account pauses sending automatically until it is topped up."
					sx={{ maxWidth: 260 }}
				/>
				{config.balance && (
					<Typography variant="caption" color="textSecondary" style={{ paddingTop: '0.5rem' }}>
						Last balance read by the dispatcher:{' '}
						{config.balance.amount !== null ? `${config.balance.amount} ${config.balance.currency ?? ''}` : 'unknown'}
						{config.balance.at ? ` · ${new Date(config.balance.at).toLocaleString()}` : ''}
					</Typography>
				)}
			</FlexLayout>

			<CardSectionTitle>2. Sender</CardSectionTitle>
			{catalog ? (
				<TextField
					select
					fullWidth
					size="small"
					label="WhatsApp Business number"
					value={senders.some((sender) => sender.phone_number === senderNumber) ? senderNumber : ''}
					onChange={(event) => chooseSender(event.target.value)}
				>
					{senders.length === 0 && (
						<MenuItem value="" disabled>
							No phone numbers on this account
						</MenuItem>
					)}
					{senders.map((sender) => (
						<MenuItem
							key={sender.id}
							value={sender.phone_number}
							disabled={sender.status !== 'CONNECTED'}
						>
							{sender.display_name || sender.display_phone_number} —{' '}
							{sender.display_phone_number}
							<StatusChip
								size="small"
								label={`${sender.status}${
									sender.quality_rating ? ` · ${sender.quality_rating}` : ''
								}`}
								color={sender.status === 'CONNECTED' ? 'success' : 'default'}
							/>
						</MenuItem>
					))}
				</TextField>
			) : (
				<StoredValue>
					{senderNumber
						? `${senderName ? `${senderName} — ` : ''}${senderNumber}`
						: 'Not chosen yet'}
					<CardNote>Load from YCloud to change the sender.</CardNote>
				</StoredValue>
			)}

			<CardSectionTitle>3. Template</CardSectionTitle>
			{catalog ? (
				<TextField
					select
					fullWidth
					size="small"
					label="Message template"
					value={selectedTemplate ? templateKey(templateName, templateLanguage) : ''}
					onChange={(event) => chooseTemplate(event.target.value)}
					helperText={
						wabaId
							? 'Templates of the chosen sender’s WhatsApp Business Account. Only APPROVED templates can be enabled.'
							: 'Choose a sender first to filter templates to its account.'
					}
				>
					{templates.length === 0 && (
						<MenuItem value="" disabled>
							No templates found
						</MenuItem>
					)}
					{templates.map((template) => (
						<MenuItem
							key={templateKey(template.name, template.language)}
							value={templateKey(template.name, template.language)}
							disabled={template.status !== 'APPROVED'}
						>
							{template.name} ({template.language})
							{template.category ? ` — ${template.category}` : ''}
							<StatusChip
								size="small"
								label={template.status}
								color={template.status === 'APPROVED' ? 'success' : 'default'}
							/>
						</MenuItem>
					))}
				</TextField>
			) : (
				<StoredValue>
					{templateName ? `${templateName} (${templateLanguage})` : 'Not chosen yet'}
					<CardNote>Load from YCloud to change the template or its variables.</CardNote>
				</StoredValue>
			)}
			{selectedTemplate &&
				(selectedTemplate.has_header_params || selectedTemplate.has_button_params) && (
					<Alert severity="warning">
						This template has header or button variables. Only body variables
						are supported — choose a template whose variables are all in the
						message body.
					</Alert>
				)}

			<CardSectionTitle>4. Variables</CardSectionTitle>
			{selectedTemplate ? (
				<TemplateMappingTable
					bodyText={selectedTemplate.body_text}
					placeholderCount={selectedTemplate.body_param_count}
					placeholderNames={selectedTemplate.body_param_names}
					templateLanguage={selectedTemplate.language}
					variables={variables}
					mapping={mapping}
					onChange={setMapping}
				/>
			) : mapping.length ? (
				<StoredValue>
					{mapping.map((key, index) => (
						<div key={index}>
							<code>{`{{${index + 1}}}`}</code> →{' '}
							{variables.find((variable) => variable.key === key)?.label ?? key}
						</div>
					))}
				</StoredValue>
			) : (
				<CardNote>Choose a template to assign its placeholders.</CardNote>
			)}

			<CardSectionTitle>5. Center-attendance template (optional)</CardSectionTitle>
			<CardNote>
				Used by a center lecture's Reports → Attendance tab to message parents
				(attendance, homework, exam score). Leave on “None” if the platform
				does not send attendance messages.
			</CardNote>
			{catalog ? (
				<TextField
					select
					fullWidth
					size="small"
					label="Attendance message template"
					value={
						selectedAttendanceTemplate
							? templateKey(attendanceTemplateName, attendanceTemplateLanguage)
							: ''
					}
					onChange={(event) => chooseAttendanceTemplate(event.target.value)}
				>
					<MenuItem value="">None — attendance messages off</MenuItem>
					{templates.map((template) => (
						<MenuItem
							key={templateKey(template.name, template.language)}
							value={templateKey(template.name, template.language)}
							disabled={template.status !== 'APPROVED'}
						>
							{template.name} ({template.language})
							{template.category ? ` — ${template.category}` : ''}
							<StatusChip
								size="small"
								label={template.status}
								color={template.status === 'APPROVED' ? 'success' : 'default'}
							/>
						</MenuItem>
					))}
				</TextField>
			) : (
				<StoredValue>
					{attendanceTemplateName
						? `${attendanceTemplateName} (${attendanceTemplateLanguage})`
						: 'None'}
					<CardNote>Load from YCloud to change the attendance template.</CardNote>
				</StoredValue>
			)}
			{selectedAttendanceTemplate &&
				(selectedAttendanceTemplate.has_header_params ||
					selectedAttendanceTemplate.has_button_params) && (
					<Alert severity="warning">
						This template has header or button variables. Only body variables
						are supported.
					</Alert>
				)}
			{selectedAttendanceTemplate ? (
				<TemplateMappingTable
					bodyText={selectedAttendanceTemplate.body_text}
					placeholderCount={selectedAttendanceTemplate.body_param_count}
					placeholderNames={selectedAttendanceTemplate.body_param_names}
					templateLanguage={selectedAttendanceTemplate.language}
					variables={attendanceVariables}
					mapping={attendanceMapping}
					onChange={setAttendanceMapping}
				/>
			) : attendanceTemplateName && attendanceMapping.length ? (
				<StoredValue>
					{attendanceMapping.map((key, index) => (
						<div key={index}>
							<code>{`{{${index + 1}}}`}</code> →{' '}
							{attendanceVariables.find((variable) => variable.key === key)
								?.label ?? key}
						</div>
					))}
				</StoredValue>
			) : null}

			<CardSectionTitle>6. Delivery status webhooks</CardSectionTitle>
			<FlexLayout alignItems="flex-start" gridGap="1rem">
				<TextField
					fullWidth
					size="small"
					label="Student API base URL (exam results)"
					placeholder="https://api.example.com"
					value={baseUrl}
					onChange={(event) => setBaseUrl(event.target.value)}
					helperText="The public origin of the student backend; the webhook path is added automatically."
				/>
				<EdButton
					variant="outlined"
					edcolor="purple"
					disabled={!keyAvailable || !baseUrl.trim() || registering}
					onClick={registerWebhook}
					style={{ whiteSpace: 'nowrap', marginTop: '0.25rem' }}
				>
					{registering ? 'Registering…' : 'Register webhook'}
				</EdButton>
			</FlexLayout>
			{webhookRegistered ? (
				<Alert severity="success">
					Registered · {config.webhook_url}
					{config.webhook_endpoint_id ? ` (endpoint ${config.webhook_endpoint_id})` : ''}
				</Alert>
			) : (
				<CardNote>
					Creates (or reuses) a YCloud webhook endpoint subscribed to
					whatsapp.message.updated and stores its signing secret. Without it,
					messages never progress past “accepted”.
				</CardNote>
			)}

			<FlexLayout alignItems="flex-start" gridGap="1rem">
				<TextField
					fullWidth
					size="small"
					label="Admin API base URL (attendance)"
					placeholder="https://api.admin.example.com"
					value={attendanceBaseUrl}
					onChange={(event) => setAttendanceBaseUrl(event.target.value)}
					helperText="The public origin of THIS (admin) backend — the attendance dispatcher lives here; the webhook path is added automatically."
				/>
				<EdButton
					variant="outlined"
					edcolor="purple"
					disabled={!keyAvailable || !attendanceBaseUrl.trim() || registeringAttendance}
					onClick={registerAttendanceWebhook}
					style={{ whiteSpace: 'nowrap', marginTop: '0.25rem' }}
				>
					{registeringAttendance ? 'Registering…' : 'Register attendance webhook'}
				</EdButton>
			</FlexLayout>
			{attendanceWebhookRegistered ? (
				<Alert severity="success">
					Attendance webhook registered · {config.attendance_webhook_url}
					{config.attendance_webhook_endpoint_id
						? ` (endpoint ${config.attendance_webhook_endpoint_id})`
						: ''}
				</Alert>
			) : (
				<CardNote>
					The attendance channel needs its own endpoint on the ADMIN API —
					without it, attendance messages never progress past “accepted”.
				</CardNote>
			)}

			{enabled && !mappingComplete && (
				<Alert severity="info">
					Every placeholder must be assigned before the channel can be enabled.
				</Alert>
			)}
			{enabled && !attendanceMappingComplete && (
				<Alert severity="info">
					Every placeholder of the attendance template must be assigned (or the
					template set to None) before the channel can be enabled.
				</Alert>
			)}
		</ProviderCard>
	);
};

const StatusChip = styled(Chip)`
	margin-inline-start: 0.5rem;
	height: 20px;
`;

const StoredValue = styled.div`
	font-size: 0.875rem;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
`;

export default YCloudCard;
